import React from 'react';
import { Clock, Map, Ticket, CreditCard, Navigation, Lightbulb, Smartphone, Shield, Zap } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export const Features = () => {
  const mainFeatures = [
    {
      icon: Clock,
      title: 'Real-Time ETA',
      description: 'Get accurate arrival predictions across all transport modes with live GPS tracking',
      details: ['Live vehicle tracking', 'Delay notifications', 'Historical accuracy data', 'Weather-adjusted predictions']
    },
    {
      icon: Map,
      title: 'Smart Route Planning',
      description: 'AI-powered route optimization considering time, cost, and convenience factors',
      details: ['Multi-modal combinations', 'Traffic-aware routing', 'Cost comparison', 'Alternative options']
    },
    {
      icon: Ticket,
      title: 'Unified Booking',
      description: 'Book trains, buses, flights, and cabs from a single platform',
      details: ['Instant confirmations', 'Digital tickets', 'Easy cancellations', 'Booking history']
    },
    {
      icon: CreditCard,
      title: 'Integrated Payments',
      description: 'Secure payment processing with multiple options and wallet integration',
      details: ['Multiple payment methods', 'Saved payment options', 'Transaction history', 'Refund processing']
    },
    {
      icon: Navigation,
      title: 'Live Journey Tracking',
      description: 'Track your entire journey from start to finish with real-time updates',
      details: ['Step-by-step navigation', 'Transfer notifications', 'Arrival alerts', 'Share trip status']
    },
    {
      icon: Lightbulb,
      title: 'Smart Recommendations',
      description: 'Personalized suggestions based on your travel patterns and preferences',
      details: ['Frequent routes', 'Time-saving options', 'Cost-efficient alternatives', 'Eco-friendly choices']
    }
  ];

  const additionalFeatures = [
    { icon: Smartphone, title: 'Mobile-First Design', description: 'Optimized for seamless mobile experience' },
    { icon: Shield, title: 'Secure & Private', description: 'Your data is encrypted and protected' },
    { icon: Zap, title: 'Lightning Fast', description: 'Quick searches and instant results' }
  ];

  const faqs = [
    {
      question: 'How does multi-modal route planning work?',
      answer: 'Our AI algorithm analyzes thousands of possible route combinations across trains, buses, metros, cabs, and flights to find the optimal journey based on your preferences for time, cost, or convenience.'
    },
    {
      question: 'Is my payment information secure?',
      answer: 'Yes, we use bank-grade encryption and comply with PCI DSS standards. We never store your complete card details, and all transactions are processed through secure payment gateways.'
    },
    {
      question: 'Can I book tickets for all transport modes?',
      answer: 'Currently, we support booking for trains, buses, flights, and cabs through our partner integrations. Metro tickets can be planned but need to be purchased separately at stations.'
    },
    {
      question: 'What happens if I miss a connection?',
      answer: 'Our live tracking system monitors your journey and automatically suggests alternative connections if delays occur. You can quickly rebook the next leg of your journey through the app.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1B4F72] to-[#138808] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Our Offerings</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed">
            Three core pillars powering India's unified mobility ecosystem
          </p>
        </div>
      </section>

      {/* Three Core Pillars */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8 mb-20">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF9933] to-[#E68A2E] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Zap className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">SaaS + APIs</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Neutral integration layer for RTCs, city bus operators, cooperatives, and MaaS platforms.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Real-time data aggregation
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Scheduling & routing APIs
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    No lock-in, full operator control
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    White-label integration
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 ring-4 ring-[#FF9933]">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1B4F72] to-[#2E86AB] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Smartphone className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">Consumer Platform</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Single app for all journey types - Bus, Metro, Auto, Cab, and more.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Multi-modal journey planning
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Unified payments via wallet/UPI
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Real-time ETAs & alerts
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Crowding & service signals
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#138808] to-[#0F6806] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Shield className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">R&D + Hardware</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Future-ready infrastructure with IoT devices and smart systems.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    GPS & telemetry sensors
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Passenger info systems
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    RFID/QR validation
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Autonomous vehicle readiness
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#FF9933] font-semibold text-sm uppercase tracking-wider">Platform Features</span>
            <h2 className="text-4xl font-bold text-[#1B4F72] mt-3 mb-4">Complete Feature Set</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Comprehensive tools and capabilities for seamless multi-modal mobility
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#FF9933] to-[#E68A2E] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-[#1B4F72] mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">{feature.description}</p>
                    <ul className="space-y-2">
                      {feature.details.map((detail, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600">
                          <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2"></span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-[#1B4F72] mb-12 text-center">And Much More</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#138808] to-[#0F6806] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1B4F72] mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-[#1B4F72] mb-12 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg shadow-md border-0 px-6">
                <AccordionTrigger className="text-lg font-semibold text-[#1B4F72] hover:text-[#FF9933] transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};
